import React, { useState, useMemo } from 'react';
import './OverviewCalendar.css';

interface OverviewCalendarProps {
  reservations: { date: string }[];
  highlightedDate: string | null;
  onDateHighlight: (date: string | null) => void;
  onDateSelect: (date: Date) => void;
}

// Helper function to format a Date object to 'YYYY-MM-DD' in the local timezone
const formatToLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const OverviewCalendar = ({ reservations, highlightedDate, onDateHighlight, onDateSelect }: OverviewCalendarProps) => {
  const [viewDate, setViewDate] = useState(new Date());

  const reservationDates = useMemo(() => 
    new Set(reservations.map(r => r.date)), 
    [reservations]
  );

  const changeMonth = (amount: number) => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + amount, 1));
  };

  const renderDays = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="day-cell empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const thisDate = new Date(year, month, day);
      // --- THIS IS THE FIX ---
      // Use the timezone-safe helper instead of toISOString()
      const dateString = formatToLocalDateString(thisDate);
      // ---------------------
      const hasReservation = reservationDates.has(dateString);

      let classNames = 'day-cell';
      if (hasReservation) classNames += ' has-reservation';
      if (dateString === highlightedDate) classNames += ' active-highlight';

      days.push(
        <div 
          key={day} 
          className={classNames}
          onMouseEnter={() => hasReservation && onDateHighlight(dateString)}
          onClick={() => onDateSelect(thisDate)}
        >
          <span>{day}</span>
        </div>
      );
    }
    return days;
  };

  const monthName = viewDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="overview-calendar-widget">
        <div className="calendar-header">
            <button onClick={() => changeMonth(-1)}>‹ Prev</button>
            <h2>{monthName}</h2>
            <button onClick={() => changeMonth(1)}>Next ›</button>
        </div>
        <div className="calendar-weekdays">
            <div>Sun</div><div>Mon</div><div>Tue</div><div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div>
        </div>
        <div className="calendar-grid" onMouseLeave={() => onDateHighlight(null)}>
            {renderDays()}
        </div>
    </div>
  );
};